import type { PptxAsset } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { produce } from "immer";

import type { Write } from "../store/zustand.helpers";
import { zustandStore } from "../store/zustand.helpers";

export type WorkbenchState = {
    pptxAssets: Map<string, PptxAsset>;
    opportunityId: string;
    title: string;
};

export type FocusModeAsset = {
    identifier: string;
    slide: number;
    previewUrl: string;
};

export interface WorkbenchActions {
    addPptxAsset: (asset: PptxAsset) => void;
    getSlideAssets: () => FocusModeAsset[];
    setOpportunityId: (opportunityId: string) => void;
    setTitle: (title: string) => void;
    resetWorkbench: () => void;
    removeSlide: (identifier: string, index: number) => void;
    getSlideIndexes: (identifier: string) => Set<number>;
    getTotalSlides: () => number;
    getSlideAssetByIndex: (slideIndex: number) => FocusModeAsset;
}

export const initialState: WorkbenchState = {
    pptxAssets: new Map(),
    opportunityId: "",
    title: "",
};

export const { store: workbenchStore } = zustandStore<
    WorkbenchState,
    WorkbenchActions
>("Workbench", initialState, (set: any, get) => ({
    addPptxAsset: (asset: PptxAsset) =>
        set(() => {
            const state: WorkbenchState = get();
            return {
                pptxAssets: new Map(state.pptxAssets).set(
                    asset.identifier!,
                    asset,
                ),
            };
        }),
    getSlideAssets: () => {
        const store = get() as Write<WorkbenchState, WorkbenchActions>;
        const assets = [...store.pptxAssets.values()];
        const focusModeAssets: FocusModeAsset[] = assets.flatMap((asset) => {
            return asset.slideNumbers!.map((slide) => ({
                identifier: asset.identifier!,
                slide,
                previewUrl: asset.previewUrl!,
            }));
        });
        return focusModeAssets;
    },
    setOpportunityId: (opportunityId) => set(() => ({ opportunityId })),
    setTitle: (title) => set(() => ({ title })),
    resetWorkbench: () =>
        set(() => {
            return { ...initialState };
        }),
    removeSlide: (identifier: string, index: number) =>
        set(() => {
            const store = get() as Write<WorkbenchState, WorkbenchActions>;
            return produce(store, (draft) => {
                const currentAsset = store.pptxAssets.get(identifier);
                if (currentAsset && currentAsset.slideNumbers) {
                    draft.pptxAssets.set(identifier, {
                        ...currentAsset,
                        slideNumbers: currentAsset.slideNumbers.filter(
                            (i) => i !== index,
                        ),
                    });
                    if (currentAsset.slideNumbers.length === 1) {
                        draft.pptxAssets.delete(identifier);
                    }
                }
                return draft;
            });
        }),
    getSlideIndexes: (identifier: string) => {
        const store = get() as Write<WorkbenchState, WorkbenchActions>;
        return new Set(store.pptxAssets.get(identifier)?.slideNumbers);
    },
    getTotalSlides: () => {
        const store = get() as Write<WorkbenchState, WorkbenchActions>;
        return store.getSlideAssets().length;
    },
    getSlideAssetByIndex: (slideIndex: number) => {
        const store = get() as Write<WorkbenchState, WorkbenchActions>;
        return store.getSlideAssets()[slideIndex];
    },
}));
